// client/src/components/Header.js - Angepasst für Ant Design
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated, getCurrentUser, logout } from '../services/authService';
import { Layout, Menu, Button, Grid, Drawer, Avatar } from 'antd'; // Importiere Antd Komponenten
import { MenuOutlined, UserOutlined, ShopOutlined, LoginOutlined, LogoutOutlined, PlusCircleOutlined, SettingOutlined, HomeOutlined, InfoCircleOutlined, ContactsOutlined, MessageOutlined, ToolOutlined } from '@ant-design/icons'; // Importiere Icons
// Entferne den Import für Header.css, da Ant Design das Styling übernimmt (ggf. wenige benutzerdefinierte Styles hinzufügen)
// import './Header.css';

const { Header: AntHeader } = Layout; // Header von Antd umbenennen, um Kollision zu vermeiden
const { useBreakpoint } = Grid; // Hook für Responsive Design

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false); // Zustand für Mobile Drawer
    const [user, setUser] = useState(null);
    const [hasShops, setHasShops] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();
    const navigate = useNavigate();
    const screens = useBreakpoint(); // Aktuelle Breakpoints (xs, sm, md, lg, xl, xxl)

    useEffect(() => {
        const loadUserData = async () => {
            try {
                setIsLoading(true);
                const authStatus = await isAuthenticated();

                if (authStatus) {
                    const currentUser = await getCurrentUser();
                    setUser(currentUser);
                    setHasShops(currentUser?.shops?.length > 0);
                } else {
                    setUser(null);
                    setHasShops(false);
                }
            } catch (error) {
                console.error("Fehler beim Laden der Benutzerdaten:", error);
                setUser(null);
                setHasShops(false);
            } finally {
                setIsLoading(false);
            }
        };

        loadUserData();
    }, [location.pathname]); // Bei Routenänderung aktualisieren

    const handleLogout = async () => {
        try {
            await logout();
            setUser(null);
            setHasShops(false);
            navigate('/'); // Navigiere zur Startseite nach Logout
        } catch (error) {
            console.error("Abmeldefehler:", error);
        }
    };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    // --- Menu Items Definition ---
    const commonMenuItems = [
        { key: '/', label: <Link to="/" onClick={closeMenu}>Startseite</Link>, icon: <HomeOutlined /> },
        { key: '/shops', label: <Link to="/shops" onClick={closeMenu}>Shops finden</Link>, icon: <ShopOutlined /> },
        { key: '/shop-melden', label: <Link to="/shop-melden" onClick={closeMenu}>Shop melden</Link>, icon: <MessageOutlined /> },
        { key: '/about', label: <Link to="/about" onClick={closeMenu}>Über Snus</Link>, icon: <InfoCircleOutlined /> },
        { key: '/contact', label: <Link to="/contact" onClick={closeMenu}>Kontakt</Link>, icon: <ContactsOutlined /> },
    ];

    const authMenuItemsLoggedIn = [
        { key: '/dashboard', label: <Link to="/dashboard" onClick={closeMenu}>Dashboard</Link>, icon: <SettingOutlined /> },
        // Admin-Link nur anzeigen, wenn der Benutzer die Rolle 'admin' hat
        user?.role === 'admin' && { key: '/admin/reported-shops', label: <Link to="/admin/reported-shops" onClick={closeMenu}>Admin</Link>, icon: <ToolOutlined /> },
        { key: 'logout', label: 'Abmelden', icon: <LogoutOutlined />, onClick: handleLogout },
    ].filter(Boolean);

    const authMenuItemsLoggedOut = [
        { key: '/user-register', label: <Link to="/user-register" onClick={closeMenu}>Registrieren</Link>, icon: <PlusCircleOutlined /> },
        { key: '/login', label: <Link to="/login" onClick={closeMenu}>Anmelden</Link>, icon: <LoginOutlined /> },
    ];

    // Menüpunkte für den Drawer (mobil) zusammenstellen
    const drawerMenuItems = [
        ...commonMenuItems,
        ...(!user ? authMenuItemsLoggedOut : authMenuItemsLoggedIn),
        // Shop registrieren Button nur anzeigen, wenn eingeloggt und weniger als 3 Shops
        (user && user.role !== 'admin' && user.shops?.length < 3) && { key: '/register', label: <Link to="/register" onClick={closeMenu}>Shop registrieren</Link>, icon: <PlusCircleOutlined /> }
    ].filter(Boolean); // Entfernt falsy values (z.B. wenn der letzte Punkt nicht hinzugefügt wird)

    // --- Desktop Navigation ---
    const DesktopNav = () => (
        <Menu
            theme="light" // oder "dark", je nach Header-Farbe
            mode="horizontal"
            selectedKeys={[location.pathname]}
            items={commonMenuItems}
            style={{ borderBottom: 'none', flexGrow: 1 }} // Flex Grow hinzugefügt, lineHeight entfernt
            overflowedIndicator={<MenuOutlined />} // Zeigt Burger-Icon, wenn nicht genug Platz
        />
    );

    // --- Auth Buttons (Desktop) ---
    const AuthButtons = () => {
        if (isLoading) return <div className="loading-spinner-small"></div>; // TODO: Antd Spinner verwenden

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Shop melden Button - Immer sichtbar */}
                <Link to="/shop-melden">
                    <Button type="default" icon={<MessageOutlined />} onClick={closeMenu}>
                        Shop melden
                    </Button>
                </Link>

                {/* Shop Registrieren Button nur anzeigen, wenn eingeloggt und weniger als 3 Shops */}
                {user && user.role !== 'admin' && user.shops?.length < 3 && (
                     <Link to="/register">
                        <Button type="primary" icon={<ShopOutlined />} onClick={closeMenu}>
                           Shop registrieren
                        </Button>
                     </Link>
                )}
                {user ? (
                    <>
                        {/* Admin Button nur für Admins */} 
                        {user.role === 'admin' && (
                            <Link to="/admin/reported-shops">
                                <Button icon={<ToolOutlined />} onClick={closeMenu} type="ghost" danger>
                                    Admin Panel
                                </Button>
                            </Link>
                        )}
                        <Link to="/dashboard">
                            <Button icon={<SettingOutlined />} onClick={closeMenu}>
                                Dashboard
                            </Button>
                        </Link>
                        <Button danger icon={<LogoutOutlined />} onClick={handleLogout}>
                            Abmelden
                        </Button>
                    </>
                ) : (
                    <>
                        <Link to="/user-register">
                           <Button onClick={closeMenu} icon={<PlusCircleOutlined />}>Registrieren</Button>
                        </Link>
                         <Link to="/login">
                             <Button type="default" icon={<LoginOutlined />} onClick={closeMenu}>
                                Anmelden
                            </Button>
                        </Link>
                    </>
                )}
            </div>
        );
    };


    return (
        <AntHeader style={{ background: '#fff', padding: '0 20px', display: 'flex', alignItems: 'center', position: 'sticky', top: 0, zIndex: 2000, height: '120px', boxShadow: '0 2px 8px #f0f1f2' }}> {/* Höhe auf 120px erhöht, zIndex nochmals erhöht */}
            {/* Logo */}
            <div className="logo" style={{ display: 'flex', alignItems: 'center', marginRight: '24px' }}> {/* lineHeight entfernt */}
                 <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
                    {/* Stelle sicher, dass das Logo zugänglich ist, ggf. Pfad anpassen */}
                    <img src="/images/snus-finder-logo.svg" alt="Snus Finder" style={{ height: '110px', marginRight: '8px' }} /> {/* Höhe auf 110px gesetzt */}
                    {/* Optional: Text-Logo anzeigen, wenn Platz ist - entfernt */}
                    {/* {screens.md && <span style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--primary-color)' }}>Snus Finder</span>} */}
                </Link>
            </div>

            {/* Desktop Navigation oder Burger Icon */}
            {screens.lg ? (
                <>
                    <DesktopNav />
                    <AuthButtons />
                </>
            ) : (
                 <>
                    <div style={{ flexGrow: 1 }}></div> {/* Spacer */}
                    <Button type="text" icon={<MenuOutlined />} onClick={toggleMenu} style={{ fontSize: '20px' }}/>
                 </>
            )}

            {/* Mobile Drawer */}
             <Drawer
                title="Menü"
                placement="right"
                onClose={closeMenu}
                open={isMenuOpen && !screens.lg}
                bodyStyle={{ padding: 0 }}
                zIndex={2001}
            >
                <Menu
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={drawerMenuItems} // Verwende die kombinierten Menüpunkte
                />
            </Drawer>
        </AntHeader>
    );
};

export default Header;